/**
 * PDFViewer Component
 *
 * Renders PDF documents using PDF.js with support for:
 * - Canvas-based rendering
 * - Text layer for selection
 * - Zoom controls
 * - Page navigation
 * - Redaction overlays
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import type { BoundingBox, Redaction, RedactionTool, EntitySuggestion } from '../../types';
import { ENTITY_TYPE_CONFIG } from '../../types';

// Configure PDF.js worker using Vite's URL import for proper bundling
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface PDFViewerProps {
  pdfData: ArrayBuffer | Uint8Array | string;
  currentPage: number;
  zoom: number;
  activeTool: RedactionTool;
  redactions: Redaction[];
  suggestions?: EntitySuggestion[];
  selectedSuggestion?: number | null;
  onPageChange: (page: number) => void;
  onDocumentLoad: (numPages: number) => void;
  onRedactionAdd: (redaction: Omit<Redaction, 'id' | 'createdAt'>) => void;
  onRedactionSelect: (id: string | null) => void;
  onSuggestionClick?: (index: number) => void;
  selectedRedaction: string | null;
  onError: (error: string) => void;
}

interface TextItem {
  str: string;
  transform: number[];
  width: number;
  height: number;
}

export function PDFViewer({
  pdfData,
  currentPage,
  zoom,
  activeTool,
  redactions,
  suggestions = [],
  selectedSuggestion,
  onPageChange: _onPageChange,
  onDocumentLoad,
  onRedactionAdd,
  onRedactionSelect,
  onSuggestionClick,
  selectedRedaction,
  onError,
}: PDFViewerProps) {
  // _onPageChange is available for future internal navigation features
  void _onPageChange;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textLayerRef = useRef<HTMLDivElement>(null);
  const redactionLayerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageRendering, setPageRendering] = useState(false);
  const [pageNumPending, setPageNumPending] = useState<number | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [currentDrawRect, setCurrentDrawRect] = useState<BoundingBox | null>(null);
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  // Load PDF document
  useEffect(() => {
    let currentPdfDoc: PDFDocumentProxy | null = null;
    let isCancelled = false;

    const loadPdf = async () => {
      try {
        // Create a copy of the buffer to prevent "Buffer is already detached" errors
        // PDF.js transfers the buffer to its web worker, which detaches the original
        // This copy ensures each load attempt has its own buffer
        let data: Uint8Array | string;
        if (pdfData instanceof ArrayBuffer) {
          data = new Uint8Array(pdfData.slice(0)); // .slice(0) creates a copy
        } else if (pdfData instanceof Uint8Array) {
          data = new Uint8Array(pdfData.buffer.slice(0)); // Copy the underlying buffer
        } else {
          data = pdfData; // string URL
        }

        const loadingTask = pdfjsLib.getDocument({ data });
        const pdf = await loadingTask.promise;

        if (isCancelled) {
          pdf.destroy();
          return;
        }

        currentPdfDoc = pdf;
        setPdfDoc(pdf);
        onDocumentLoad(pdf.numPages);
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to load PDF:', err);
          onError(`Failed to load PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      }
    };

    loadPdf();

    return () => {
      isCancelled = true;
      if (currentPdfDoc) {
        currentPdfDoc.destroy();
      }
    };
  }, [pdfData, onDocumentLoad, onError]);

  // Render page
  const renderPage = useCallback(async (pageNum: number) => {
    if (!pdfDoc || !canvasRef.current || !textLayerRef.current) return;

    setPageRendering(true);

    try {
      const page = await pdfDoc.getPage(pageNum);
      const scale = zoom / 100;
      const viewport = page.getViewport({ scale });

      // Set canvas dimensions
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      if (!context) {
        setPageRendering(false);
        return;
      }

      canvas.height = viewport.height;
      canvas.width = viewport.width;
      setPageSize({ width: viewport.width, height: viewport.height });

      // Render PDF page to canvas
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      // Render text layer for selection
      await renderTextLayer(page, viewport);

      setPageRendering(false);
    } catch (err) {
      console.error('Error rendering page:', err);
      setPageRendering(false);
      onError(`Failed to render page: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [pdfDoc, zoom]);

  // Render text layer for text selection
  const renderTextLayer = async (page: PDFPageProxy, viewport: ReturnType<PDFPageProxy['getViewport']>) => {
    if (!textLayerRef.current) return;

    // Clear existing text layer
    textLayerRef.current.innerHTML = '';
    textLayerRef.current.style.width = `${viewport.width}px`;
    textLayerRef.current.style.height = `${viewport.height}px`;

    try {
      const textContent = await page.getTextContent();

      textContent.items.forEach((item) => {
        const textItem = item as TextItem;
        if (!textItem.str) return;

        const tx = pdfjsLib.Util.transform(
          viewport.transform,
          textItem.transform
        );

        const span = document.createElement('span');
        span.textContent = textItem.str;
        span.style.position = 'absolute';
        span.style.left = `${tx[4]}px`;
        span.style.top = `${tx[5] - textItem.height}px`;
        span.style.fontSize = `${Math.abs(tx[0])}px`;
        span.style.fontFamily = 'sans-serif';
        span.style.color = 'transparent';
        span.style.whiteSpace = 'pre';
        span.style.pointerEvents = 'all';
        span.style.cursor = activeTool === 'select' ? 'text' : 'crosshair';

        textLayerRef.current?.appendChild(span);
      });
    } catch (err) {
      console.error('Error rendering text layer:', err);
    }
  };

  // Effect to render page when page number or zoom changes
  useEffect(() => {
    if (!pdfDoc) return;

    if (pageRendering) {
      setPageNumPending(currentPage);
    } else {
      setPageNumPending(null); // Clear any pending page
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage, zoom, renderPage]);

  // Effect to handle pending page after render completes
  useEffect(() => {
    if (!pageRendering && pageNumPending !== null) {
      const pending = pageNumPending;
      setPageNumPending(null);
      renderPage(pending);
    }
  }, [pageRendering, pageNumPending, renderPage]);

  // Get the current scale factor for coordinate normalization
  const scale = zoom / 100;

  // Handle mouse events for drawing redactions
  const getMousePosition = (e: React.MouseEvent): { x: number; y: number } => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (activeTool === 'select') return;

    const pos = getMousePosition(e);
    setIsDrawing(true);
    setDrawStart(pos);
    setCurrentDrawRect({ x: pos.x, y: pos.y, width: 0, height: 0 });
    onRedactionSelect(null);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !drawStart) return;

    const pos = getMousePosition(e);
    const width = pos.x - drawStart.x;
    const height = pos.y - drawStart.y;

    setCurrentDrawRect({
      x: width >= 0 ? drawStart.x : pos.x,
      y: height >= 0 ? drawStart.y : pos.y,
      width: Math.abs(width),
      height: Math.abs(height),
    });
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentDrawRect) {
      setIsDrawing(false);
      setDrawStart(null);
      setCurrentDrawRect(null);
      return;
    }

    // Only add redaction if it has meaningful size
    if (currentDrawRect.width > 5 && currentDrawRect.height > 5) {
      // Normalize bounds to base scale (100%) for storage
      // This ensures bounds remain consistent regardless of current zoom level
      const normalizedBounds = {
        x: currentDrawRect.x / scale,
        y: currentDrawRect.y / scale,
        width: currentDrawRect.width / scale,
        height: currentDrawRect.height / scale,
      };
      onRedactionAdd({
        type: activeTool,
        page: currentPage,
        bounds: normalizedBounds,
        color: getToolColor(activeTool),
        opacity: getToolOpacity(activeTool),
      });
    }

    setIsDrawing(false);
    setDrawStart(null);
    setCurrentDrawRect(null);
  };

  const handleRedactionClick = (e: React.MouseEvent, redactionId: string) => {
    e.stopPropagation();
    onRedactionSelect(redactionId);
  };

  // Get tool-specific colors and opacity
  const getToolColor = (tool: RedactionTool): string => {
    switch (tool) {
      case 'highlight':
        return '#FFFF00';
      case 'box':
        return '#FF0000';
      case 'blackout':
        return '#000000';
      default:
        return '#000000';
    }
  };

  const getToolOpacity = (tool: RedactionTool): number => {
    switch (tool) {
      case 'highlight':
        return 0.3;
      case 'box':
        return 0.3;
      case 'blackout':
        return 1;
      default:
        return 1;
    }
  };

  // Filter redactions for current page
  const pageRedactions = redactions.filter(r => r.page === currentPage);

  // Filter suggestions for current page (suggestions use 0-indexed pages)
  const pageSuggestions = suggestions.filter(s => s.page === currentPage - 1);

  const handleSuggestionClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (onSuggestionClick) {
      onSuggestionClick(index);
    }
  };

  // Get suggestion overlay style based on status
  const getSuggestionStyle = (suggestion: EntitySuggestion, isSelected: boolean) => {
    const config = ENTITY_TYPE_CONFIG[suggestion.entityType] || ENTITY_TYPE_CONFIG.CUSTOM;
    let opacity = 0.3;
    let borderStyle = 'dashed';

    if (suggestion.status === 'approved') {
      opacity = 0.5;
      borderStyle = 'solid';
    } else if (suggestion.status === 'rejected') {
      opacity = 0.1;
      borderStyle = 'dotted';
    }

    return {
      backgroundColor: config.color,
      opacity: isSelected ? opacity + 0.2 : opacity,
      borderColor: config.color,
      borderStyle,
      borderWidth: isSelected ? '3px' : '2px',
    };
  };

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      style={{ cursor: activeTool !== 'select' ? 'crosshair' : 'default' }}
    >
      {/* PDF Canvas */}
      <canvas
        ref={canvasRef}
        className="block"
        style={{
          width: pageSize.width,
          height: pageSize.height,
        }}
      />

      {/* Text Layer for selection */}
      <div
        ref={textLayerRef}
        className="absolute top-0 left-0 overflow-hidden"
        style={{
          pointerEvents: activeTool === 'select' ? 'auto' : 'none',
        }}
      />

      {/* Redaction Layer */}
      <div
        ref={redactionLayerRef}
        className="absolute top-0 left-0"
        style={{
          width: pageSize.width,
          height: pageSize.height,
          pointerEvents: activeTool !== 'select' ? 'auto' : 'none',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* AI Suggestion overlays */}
        {pageSuggestions.map((suggestion) => {
          if (!suggestion.bounds) return null;
          const isSelected = selectedSuggestion === suggestion.index;
          const style = getSuggestionStyle(suggestion, isSelected);

          return (
            <div
              key={`suggestion-${suggestion.index}`}
              className={`absolute transition-all cursor-pointer ${
                isSelected ? 'ring-2 ring-offset-1' : ''
              }`}
              style={{
                // Scale bounds from base (100%) to current zoom level
                // AI suggestions bounds come from backend at base scale
                left: suggestion.bounds.x * scale,
                top: suggestion.bounds.y * scale,
                width: suggestion.bounds.width * scale,
                height: suggestion.bounds.height * scale,
                ...style,
                pointerEvents: 'auto',
              }}
              onClick={(e) => handleSuggestionClick(e, suggestion.index)}
              title={`${suggestion.entityType}: ${suggestion.text} (${Math.round(suggestion.confidence * 100)}%)`}
            >
              {/* Tooltip on hover */}
              <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block bg-background border border-border rounded px-2 py-1 text-xs whitespace-nowrap z-50">
                {suggestion.entityType}: {suggestion.text}
              </div>
            </div>
          );
        })}

        {/* Existing redactions */}
        {pageRedactions.map((redaction) => (
          <div
            key={redaction.id}
            className={`absolute transition-all ${
              selectedRedaction === redaction.id ? 'ring-2 ring-blue-500' : ''
            }`}
            style={{
              // Scale bounds from base (100%) to current zoom level
              left: redaction.bounds.x * scale,
              top: redaction.bounds.y * scale,
              width: redaction.bounds.width * scale,
              height: redaction.bounds.height * scale,
              backgroundColor: redaction.color || '#000000',
              opacity: redaction.opacity || 1,
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
            onClick={(e) => handleRedactionClick(e, redaction.id)}
          />
        ))}

        {/* Current drawing rectangle */}
        {currentDrawRect && (
          <div
            className="absolute border-2 border-dashed"
            style={{
              left: currentDrawRect.x,
              top: currentDrawRect.y,
              width: currentDrawRect.width,
              height: currentDrawRect.height,
              backgroundColor: getToolColor(activeTool),
              opacity: getToolOpacity(activeTool) * 0.5,
              borderColor: getToolColor(activeTool),
              pointerEvents: 'none',
            }}
          />
        )}
      </div>

      {/* Loading overlay */}
      {pageRendering && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}
    </div>
  );
}
